import React from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Zap, Target, Clock, Trophy, ChevronRight, Gift, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_BG = "https://images.pexels.com/photos/34958805/pexels-photo-34958805.jpeg";
const INVITE_LS_KEY = "nah_invite_code";

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [invite, setInvite] = React.useState(null);
  const [agreed, setAgreed] = React.useState(false);
  const [agreeError, setAgreeError] = React.useState(false);

  React.useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  // Resolve any invite code stashed in localStorage (captured by /invite/:code)
  React.useEffect(() => {
    const code = localStorage.getItem(INVITE_LS_KEY);
    if (!code) return;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/invites/${code}`);
        setInvite({ code, ...r.data });
      } catch (_e) {
        localStorage.removeItem(INVITE_LS_KEY);
      }
    })();
  }, []);

  const handleSignIn = () => {
    if (!agreed) {
      setAgreeError(true);
      // Scroll the agreement section into view so they can tick the box
      document.getElementById("legal-agree-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0A0A0A] text-white">
      {/* Hero bg */}
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="neon city" className="w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/90 via-black/70 to-black/95" />
        <div className="absolute inset-0 grid-bg opacity-40" />
      </div>

      <div className="relative z-10">
        {/* Top nav */}
        <header className="flex items-center justify-between px-6 lg:px-14 pt-8">
          <div data-testid="landing-brand">
            <div className="label-kicker">Retro Arcade Racer</div>
            <div className="font-heading text-3xl mt-1 leading-[0.95]">
              <span className="text-[#FFCC00]">NITRO</span>{" "}
              <span className="text-white">AUTO</span>{" "}
              <span className="text-[#FF3B30]">HEIST</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline font-mono text-[11px] text-white/50 tracking-[0.3em]">v0.2 / BETA</span>
            <button
              className="btn-ghost text-xs"
              data-testid="landing-top-signin"
              onClick={handleSignIn}
            >
              Sign In
            </button>
          </div>
        </header>

        {/* Invite banner — shown when the user landed via /invite/:code */}
        {invite && (
          <div className="px-6 lg:px-14 mt-6">
            <div
              className="flex items-center gap-3 surface-glass border-l-4 border-[#FFCC00] px-4 py-3 max-w-3xl"
              data-testid="landing-invite-banner"
            >
              <Gift className="w-5 h-5 text-[#FFCC00]" />
              <div className="flex-1">
                <div className="text-[10px] tracking-[0.3em] text-[#FFCC00] uppercase">You&apos;ve been invited</div>
                <div className="font-heading text-lg mt-0.5">
                  <span className="text-white">{invite.referrer_name}</span>
                  <span className="text-white/50"> wants you in Nitro Auto Heist.</span>
                </div>
                <div className="text-xs text-white/60 mt-0.5">
                  Both of you get <span className="text-[#FFCC00] font-mono">+500 credits</span> when you sign in.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Hero */}
        <section className="px-6 lg:px-14 pt-16 lg:pt-20 pb-20 max-w-6xl">
          <div className="inline-flex items-center gap-2 surface-glass px-3 py-1.5 mb-10">
            <span className="w-1.5 h-1.5 bg-[#FF3B30] rounded-full anim-pulse-red" />
            <span className="font-mono text-[10px] tracking-[0.4em] text-white/80">REAL-TIME MULTIPLAYER · 16-BIT CHAOS</span>
          </div>
          <h1 className="font-heading text-6xl sm:text-7xl lg:text-[9rem] leading-[0.88] tracking-tighter">
            Grab the loot.<br />
            <span className="text-[#FFCC00]">Ditch</span> the <span className="text-[#FF3B30]">Feds.</span><br />
            Burn rubber.
          </h1>
          <p className="mt-10 max-w-xl text-white/70 text-base leading-relaxed">
            Race your friends live, build your own custom heists, and share them with the world. Boot up, trick out
            your rig, and pull the loot run of the decade — before the clock hits{" "}
            <span className="font-mono text-[#FFCC00]">06:20</span>.
          </p>

          <div className="mt-12 flex flex-wrap gap-4">
            <button
              className="btn-primary anim-pulse-red flex items-center gap-2"
              data-testid="landing-signin-primary"
              onClick={handleSignIn}
            >
              <Zap className="w-4 h-4" />
              {invite ? "Claim invite · Sign in" : "Sign in with Google"}
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              className="btn-ghost"
              data-testid="landing-how-it-works"
              onClick={() => {
                document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              How it works
            </button>
          </div>

          {/* Agreement gate — checkbox must be ticked before Sign in works */}
          <div id="legal-agree-section" className="mt-6 max-w-xl">
            <label
              className={`flex items-start gap-3 cursor-pointer select-none p-3 border transition-colors ${
                agreeError && !agreed
                  ? "border-[#FF3B30] bg-[#FF3B30]/5 anim-pulse-red"
                  : agreed
                    ? "border-[#FFCC00]/60 bg-[#FFCC00]/[0.04]"
                    : "border-white/10 hover:border-white/30"
              }`}
              data-testid="landing-agree-label"
            >
              <span
                className={`w-5 h-5 mt-0.5 flex-shrink-0 border-2 flex items-center justify-center ${
                  agreed ? "bg-[#FFCC00] border-[#FFCC00]" : "border-white/40"
                }`}
              >
                {agreed && <Check className="w-3.5 h-3.5 text-black" strokeWidth={3.5} />}
              </span>
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => { setAgreed(e.target.checked); setAgreeError(false); }}
                className="sr-only"
                data-testid="landing-agree-checkbox"
              />
              <span className="text-xs text-white/75 leading-relaxed">
                I&apos;m 13+ and I agree to the{" "}
                <Link to="/terms" className="text-[#FFCC00] underline underline-offset-2 hover:text-[#FFD633]" data-testid="landing-terms-link">
                  Terms of Use
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="text-[#FFCC00] underline underline-offset-2 hover:text-[#FFD633]" data-testid="landing-privacy-link">
                  Privacy Policy
                </Link>
                .
              </span>
            </label>
            {agreeError && !agreed && (
              <div className="mt-2 text-xs text-[#FF3B30] tracking-wide" data-testid="landing-agree-error">
                You need to agree before we can sign you in.
              </div>
            )}
          </div>
        </section>

        {/* Feature strip */}
        <section id="how-it-works" className="px-6 lg:px-14 pb-24">
          <div className="label-kicker mb-6">What's inside</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {[
              { icon: Clock, title: "06:20 Heist", desc: "Timer's ticking. Grab every marker before the FBI closes in." },
              { icon: Target, title: "10 Cities & Counting", desc: "Detroit, Paris, Tokyo, Cairo, LA — and six more, each with its own grid." },
              { icon: Zap, title: "Trunk Monkey", desc: "Rocket engines, guided missiles, monkey-bomb chaos. Build your rig." },
              { icon: Trophy, title: "Boss Gallery", desc: "Twenty pixel-art champs. Beat them all for the Golden Globetrotter." },
            ].map((f, i) => (
              <div key={i} className="surface-solid p-7 transition-colors duration-150 hover:bg-white/[0.03]">
                <f.icon className="w-5 h-5 text-[#FF3B30]" strokeWidth={2.2} />
                <div className="font-heading text-2xl mt-4">{f.title}</div>
                <div className="mt-2 text-sm text-white/60 leading-relaxed">{f.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Cities strip */}
        <section className="px-6 lg:px-14 pb-24">
          <div className="flex items-baseline justify-between mb-6">
            <div className="label-kicker">The Grid</div>
            <div className="font-mono text-[10px] text-white/40">10 LOCATIONS</div>
          </div>
          <div className="font-heading text-5xl lg:text-7xl">10 cities.<br />One getaway.</div>
          <div className="mt-10 flex flex-wrap gap-2">
            {[
              "Detroit", "NYC", "Los Angeles", "Mexico City", "Paris",
              "London", "Beirut", "Cairo", "Toronto", "Tokyo",
            ].map((c) => (
              <span
                key={c}
                className="px-4 py-2 border border-white/15 text-xs font-mono tracking-widest uppercase hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors duration-150"
              >
                {c}
              </span>
            ))}
          </div>
        </section>

        <footer className="border-t border-white/10 px-6 lg:px-14 py-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="font-mono text-[10px] text-white/40 tracking-[0.3em] uppercase">
            © Colin Darke Solutions LLC · Nitro Auto Heist
          </div>
          <div className="flex items-center gap-6 text-[10px] tracking-[0.25em] uppercase">
            <Link to="/terms" className="text-white/50 hover:text-[#FFCC00]" data-testid="footer-terms-link">
              Terms
            </Link>
            <Link to="/privacy" className="text-white/50 hover:text-[#FFCC00]" data-testid="footer-privacy-link">
              Privacy
            </Link>
            <button
              onClick={handleSignIn}
              data-testid="landing-footer-signin"
              className="text-white/60 hover:text-white"
            >
              Start the heist →
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
